import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { BookingDetails } from '../components/BookingDetails.tsx';
import { useMyBookings, useRooms } from '../queries.ts';
import { fmtDateTime, fmtRange } from '../time.ts';
import type { Me } from '../types.ts';

interface Notification {
  id: string;
  type: string;
  text: string;
  readAt: string | null;
  createdAt: string;
}

export function MyBookingsPage({ me }: { me: Me }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [past, setPast] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const bookings = useMyBookings(past);
  const rooms = useRooms();
  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<Notification[]>('/api/me/notifications'),
  });
  const roomName = (id: string) => rooms.data?.find((r) => r.id === id)?.name ?? '';
  const unread = (notifications.data ?? []).filter((n) => !n.readAt).length;

  return (
    <>
      <div className="bar">
        <div className="seg" role="group">
          <button type="button" aria-pressed={!past} onClick={() => setPast(false)}>
            {t('mine.upcoming')}
          </button>
          <button type="button" aria-pressed={past} onClick={() => setPast(true)}>
            {t('mine.past')}
          </button>
        </div>
      </div>
      <section className="card" aria-label={past ? t('mine.past') : t('mine.upcoming')}>
        {bookings.data?.length === 0 && <p className="k">{t('mine.none')}</p>}
        {bookings.data?.map((b) => (
          <div className="li" key={b.id}>
            <div>
              <b>{b.title || t('booking.titlePlaceholder')}</b>
              <br />
              <span className="k">
                {roomName(b.roomId)} · {fmtRange(b.start, b.end)} · {b.attendees}{' '}
                {t('common.people')}
                {b.seriesId ? ` · ${t('booking.series')}` : ''}
                {b.status === 'cancelled' ? ` · ${t('booking.statusCancelled')}` : ''}
              </span>
            </div>
            {b.status === 'active' && (
              <button className="btn alt sm" onClick={() => setOpen(b.id)}>
                {t('common.edit')}
              </button>
            )}
          </div>
        ))}
      </section>

      <section>
        <div className="bar">
          <h2 className="sp" style={{ margin: 0 }}>
            {t('mine.notifications')}
          </h2>
          {unread > 0 && (
            <button
              className="btn alt sm"
              onClick={async () => {
                await api.post('/api/me/notifications/read');
                void qc.invalidateQueries({ queryKey: ['notifications'] });
              }}
            >
              {t('mine.markRead')}
            </button>
          )}
        </div>
        <div className="card">
          {notifications.data?.length === 0 && <p className="k">{t('mine.noNotifications')}</p>}
          {notifications.data?.map((n) => (
            <div className="li" key={n.id}>
              <span>
                {!n.readAt && <span className="pill ok">•</span>} {n.text}
              </span>
              <span className="k">{fmtDateTime(n.createdAt)}</span>
            </div>
          ))}
        </div>
      </section>
      {open && (
        <BookingDetails
          me={me}
          rooms={rooms.data ?? []}
          bookingId={open}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}
